import { Badge, Box, Grid, styled, Typography } from '@mui/material';
import { AnimatePresence, m } from 'framer-motion';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import Image from '../../../components/Image';
import { varFade } from '../../../components/animate';
import { S3_ASSETS_URL } from '../../../config';
import useAuth from '../../../hooks/useAuth';
import useLocales from '../../../hooks/useLocales';
import useResponsive from '../../../hooks/useResponsive';
import { openLogin } from '../../../redux/slices/authPages';
import { openTransactionsDialog } from '../../../redux/slices/transactionsDialog';
import { useDispatch, useSelector } from '../../../redux/store';
import { appLocale } from '../../../utils/const';

const MenuGrid = styled(Grid)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  justifyContent: 'center',
}));

const LeftRightMenuBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  display: 'flex',
  width: '80%',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '2.5vw',
  top: 0,
  left: '50%',
  transform: 'translateX(-50%)',
}));

const MobileNav = () => {
  const { isAuthenticated } = useAuth();

  const dispatch = useDispatch();

  const location = useLocation();

  const { translate } = useLocales();

  const isSm = useResponsive('down', 'md');

  const navigate = useNavigate();

  const { siteSettings, promotions } = useSelector((x) => x.lookup);

  const eligibleBonusCount = useMemo(() => {
    if (!isAuthenticated) return 0;
    return promotions?.data?.filter((p) => p?.is_manual_claimable && p?.is_claimable_now)?.length || 0;
  }, [promotions, isAuthenticated]);

  const openDeposit = () => {
    if (isAuthenticated) {
      dispatch(
        openTransactionsDialog({
          open: true,
          isDeposit: true,
        })
      );
    } else {
      dispatch(openLogin());
    }
  };

  const openWithdraw = () => {
    if (isAuthenticated) {
      dispatch(
        openTransactionsDialog({
          open: true,
          isDeposit: false,
        })
      );
    } else {
      dispatch(openLogin());
    }
  };

  const navi1Width = { en: '8vw', ms: '11vw' }[appLocale] || '11vw';

  if (!isSm) return <></>;

  return (
    <>
      <AnimatePresence>
        <Box
          component={m.div}
          {...varFade().inUp}
          sx={{ position: 'fixed', bottom: 0, zIndex: 2, width: '100%', pb: '10px' }}
        >
          <Grid container>
            <MenuGrid item xs={4} sx={{ transform: 'translateX(4vw)' }}>
              <Image
                src={`${S3_ASSETS_URL}home/navigation/MobileMenuBg_v2.webp`}
                sx={{ width: { xs: '34vw' }, height: { xs: '9.9vw' } }}
              />
              <LeftRightMenuBox>
                <Box sx={{ display: 'flex', justifyContent: 'center' }} onClick={() => navigate('/')}>
                  <Image
                    sx={{
                      width: { xs: navi1Width },
                      height: { xs: '11vw' },
                      cursor: 'pointer',
                    }}
                    imgSx={{ objectFit: 'contain' }}
                    src={`${S3_ASSETS_URL}home/navigation/${appLocale}/MobileNavi-1.png`}
                  />
                </Box>
                <Box
                  sx={{ display: 'flex', justifyContent: 'center' }}
                  onClick={() => {
                    if (siteSettings?.config_telegram) {
                      window.open(siteSettings.config_telegram, '_blank');
                    } else {
                      navigate('/');
                    }
                  }}
                >
                  <Image
                    sx={{ width: { xs: '11vw' }, height: { xs: '11vw' }, cursor: 'pointer' }}
                    imgSx={{ objectFit: 'contain' }}
                    src={`${S3_ASSETS_URL}home/navigation/${appLocale}/MobileNavi-3.png`}
                  />
                </Box>
              </LeftRightMenuBox>
            </MenuGrid>
            <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box
                component={m.div}
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: { xs: '15vw' } }}
              >
                <Badge
                  badgeContent={eligibleBonusCount}
                  color="error"
                  invisible={eligibleBonusCount === 0}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '3.6vw',
                      minWidth: '6vw',
                      height: '6vw',
                      borderRadius: '6vw',
                      fontWeight: 900,
                      transform: 'scale(1) translate(-30%, 35%)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      width: { xs: '15vw' },
                      height: { xs: '15vw' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        width: '13.5vw',
                        height: '13.5vw',
                        borderRadius: '50%',
                        border: '0.7vw solid #f0cf6b',
                        boxShadow: '0 0 1.2vw rgba(240,207,107,0.75), inset 0 0 0.9vw rgba(240,207,107,0.55)',
                        pointerEvents: 'none',
                      }}
                    />
                    <Image
                      src={`${S3_ASSETS_URL}home/navigation/mobile_bonus_button.png`}
                      sx={{
                        cursor: 'pointer',
                        width: { xs: '15vw' },
                        height: { xs: '15vw' },
                        transform: 'translateY(-2vw)',
                      }}
                      imgSx={{ objectFit: 'contain' }}
                      onClick={() => navigate('/bonus')}
                    />
                  </Box>
                </Badge>
                <Typography
                  onClick={() => navigate('/bonus')}
                  sx={{
                    cursor: 'pointer',
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    fontWeight: 900,
                    fontSize: '4vw',
                    lineHeight: 1,
                    letterSpacing: '0.04em',
                    mt: '-1vw',
                    backgroundImage: 'linear-gradient(180deg, #fde7a0 0%, #e9c349 45%, #b07a13 100%)',
                    WebkitBackgroundClip: 'text',
                    backgroundClip: 'text',
                    color: 'transparent',
                    WebkitTextStroke: '0.3px #6b4a00',
                    filter: 'drop-shadow(0 0.2vw 0.15vw rgba(0,0,0,0.6))',
                  }}
                >
                  {translate('bonus')}
                </Typography>
              </Box>
            </Grid>
            <MenuGrid item xs={4} sx={{ transform: 'translateX(-4vw)' }}>
              <Image
                src={`${S3_ASSETS_URL}home/navigation/MobileMenuBg_v2.webp`}
                sx={{ width: { xs: '34vw' }, height: { xs: '9.9vw' }, transform: 'rotate(180deg)' }}
              />
              <LeftRightMenuBox>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Image
                    sx={{ width: { xs: '11vw' }, height: { xs: '11vw' }, cursor: 'pointer' }}
                    onClick={openDeposit}
                    imgSx={{ objectFit: 'contain' }}
                    src={`${S3_ASSETS_URL}home/navigation/${appLocale}/deposit-icon.png`}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Image
                    sx={{ width: { xs: '11vw' }, height: { xs: '11vw' }, cursor: 'pointer' }}
                    onClick={openWithdraw}
                    imgSx={{ objectFit: 'contain' }}
                    src={`${S3_ASSETS_URL}home/navigation/${appLocale}/withdraw-icon.png`}
                  />
                </Box>
              </LeftRightMenuBox>
            </MenuGrid>
          </Grid>
        </Box>
      </AnimatePresence>
    </>
  );
};

export default MobileNav;

export const MobileNavSpacer = () => {
  const isSm = useResponsive('down', 'md');

  if (!isSm) return <></>;

  return (
    <Grid container sx={{ display: 'flex', justifyContent: 'center', visibility: 'hidden' }}>
      <Grid item xs={4}>
        <Image
          src={require('../../../assets/logo.png')}
          sx={{ cursor: 'pointer' }}
          // onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })}
        />
      </Grid>
    </Grid>
  );
};
